import { RapidStorLocation, RapidStorUnitSortOrder } from "../../../services/rapidstor";

export default interface Location {
  name: string;
  slug: string;
  locationId: number;
  locationCode: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  websiteUrl: string;
  latitude: number;
  longitude: number;
  timezoneOffset: number;
  address: {
    address1: string;
    address2: string;
    city: string;
    region: string;
    postalCode: string;
    country: string;
  };
  deleted: boolean;
  archived: boolean;
  rapidstorEnabled: boolean;
  token: string;
  createdAt: string;
  updatedAt: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scaConfiguration: any;
  settings: {
    logo: string;
    groundFloor: number;
    groupUnitsByFloor: boolean;
    unitSortOrder: RapidStorUnitSortOrder[];
    billingDefault: string;
    sizeSymbol: string;
    areaSizeSymbol: string;
    allowMoveIn: boolean;
    showAreaInsteadOfDimensions: boolean;
  };
  rapidstor: {
    allowMoveIn: boolean;
    completeMoveinUrl: string;
    reservationNumberOfDaysInAdvanceLimit: number;
    includeReservedUnitsInOccupied: boolean;
    siteOpeningDate: string;
  };
  hours: {
    officeMondayOpen: string;
    officeTuesdayOpen: string;
    officeWednesdayOpen: string;
    officeThursdayOpen: string;
    officeFridayOpen: string;
    officeSaturdayOpen: string;
    officeSundayOpen: string;
    officeMondayClose: string;
    officeTuesdayClose: string;
    officeWednesdayClose: string;
    officeThursdayClose: string;
    officeFridayClose: string;
    officeSaturdayClose: string;
    officeSundayClose: string;
  };
}

export const formatAddress = (location: Location): string => {
  const { address1, address2, city, region, postalCode } = location.address;

  return [
    address1 ? `${address1}, ` : "",
    address2 ? `${address2}, ` : "",
    city ? `${city}, ` : "",
    region ? `${region}, ` : "",
    postalCode ? postalCode : "",
  ].join("");
};

export const locationTransformer = (location: RapidStorLocation): Location => {
  const name = location.sSiteName
    .replace(/^Surestore Consultants Ltd (- )?/i, "")
    .replace(/^Surestore (- )?/i, "")
    .replace(/^The Self Storage Company,? (- )?/i, "");
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[\s_'-]+/g, "")
    .replace(/[^\w\s-]/g, "")
    .replace(/(theselfstoragecompany)/g, "")
    .replace(/(surestore)/g, "");
  return <Location>{
    // This is not nice, but it's all we can do really
    name,
    slug: slug
      .toLowerCase()
      .trim()
      .replace(/[\s_'-]+/g, "")
      .replace(/[^\w\s-]/g, "")
      .replace(/(surestore)/g, "")
      .replace("aston", "birmingham")
      .replace("apexcorner", "edgware")
      .replace("hamstead", "hamstead-birmingham")
      .replace("hemelhempstead", "hemel-hempstead")
      .replace("tunbridgewells", "tunbridge-wells")
      .replace("westmolesey", "west-molesey")
      .replace("walthamabbey", "waltham-abbey")
      .replace("welwyngardencity", "welwyn-garden-city")
      .replace("burton", "burton-on-trent")
      .replace("oldtrafford", "trafford")
      .replace("york", "city-of-york"),
    locationId: location.SiteID,
    locationCode: location.sLocationCode,
    contactName: location.sContactName,
    contactEmail: location.sEmailAddress,
    contactPhone: location.sSitePhone,
    websiteUrl: location.sWebSiteURL,
    latitude: parseFloat(location.dcLatitude),
    longitude: parseFloat(location.dcLongitude),
    timezoneOffset: location.iGMTTimeOffset,
    address: {
      address1: location.sSiteAddr1,
      address2: location.sSiteAddr2,
      city: location.sSiteCity,
      region: location.sSiteRegion,
      postalCode: location.sSitePostalCode,
      country: location.sSiteCountry,
    },
    rapidstor: {
      allowMoveIn: location.rapidstor.allowMoveIn,
      completeMoveinUrl: location.rapidstor.completeMoveinUrl,
      reservationNumberOfDaysInAdvanceLimit:
        location.rapidstor.reservationNumberOfDaysInAdvanceLimit,
      moveinNumberOfDaysInAdvanceLimit:
        location.rapidstor.moveinNumberOfDaysInAdvanceLimit,
      includeReservedUnitsInOccupied: location.rapidstor.includeReservedUnitsInOccupied,
      siteOpeningDate: location.rapidstor.siteOpeningDate,
    },
    deleted: location.deleted,
    archived: location.bArchived == "true",
    rapidstorEnabled: location.rapidstorEnabled,
    token: location.token,
    createdAt: location.dCreated,
    updatedAt: location.dUpdated,
    scaConfiguration: location.scaConfiguration,
    settings: {
      logo: location.rapidstor.siteLogo,
      groundFloor: location.rapidstor.groundFloor || 0,
      groupUnitsByFloor: location.rapidstor.unitListing.useMultilevel,
      billingDefault: location.rapidstor.billingDefault,
      sizeSymbol: location.rapidstor.sizeSymbol,
      areaSizeSymbol: location.rapidstor.areaSizeSymbol,
      allowMoveIn: location.rapidstor.allowMoveIn,
      showAreaInsteadOfDimensions: location.rapidstor.showAreaInsteadOfDimensions,
      unitSortOrder: [
        location.rapidstor.unitListing.sortOrder.primary,
        location.rapidstor.unitListing.sortOrder.secondary,
        location.rapidstor.unitListing.sortOrder.tertiary,
      ].filter((sortOrder) => sortOrder !== "none"),
    },
    hours: location.hours,
  };
};
